import mqtt from 'mqtt';
import { appConfig } from '../config/app.config';
import { MQTTConfig, MQTTRequestEvent, MQTTResponseEvent } from './types';
import feathers from '../feathers';

// Import debugger in development
if (process.env.NODE_ENV === 'development') {
  import('./MQTTDebugger');
}

export class MQTTSubscriber {
  private static instance: MQTTSubscriber;
  private client: mqtt.MqttClient | null = null;
  private config: MQTTConfig | null = null;
  private userId: string | null = null;
  private currentClientId: string | null = null;
  private messageHandlers: Map<string, (data: any) => void> = new Map();
  private subscribedTopics: Set<string> = new Set();
  private isConnecting: boolean = false;
  private jwtToken: string | null = null;
  private tokenExpiresAt: Date | null = null;

  // Connection management
  private connectionAttempts: number = 0;
  private maxConnectionAttempts: number = 10;
  private baseRetryDelay: number = 1000; // Start with 1 second
  private maxRetryDelay: number = 60000; // Max 60 seconds
  private lastConnectionAttempt: number = 0;
  private circuitBreakerTimeout: number = 5 * 60 * 1000; // 5 minutes
  private isCircuitBreakerOpen: boolean = false;

  private constructor() {
    // Initialize basic config for testing
    this.config = {
      brokerUrl: appConfig.MQTT_BROKER_URL,
      userId: null
    };
  }

  public static getInstance(): MQTTSubscriber {
    if (!MQTTSubscriber.instance) {
      MQTTSubscriber.instance = new MQTTSubscriber();
    }
    return MQTTSubscriber.instance;
  }

  private async authenticateMqtt(userId: string): Promise<void> {
    try {
      console.log('[MQTT] Authenticating with backend for JWT token...');

      const response = await feathers.service('mqtt-auth').create({
        userId,
        role: 'user',
        permissions: ['read', 'write'],
        expiresInHours: 24
      });

      this.jwtToken = response.token;
      this.tokenExpiresAt = new Date(response.expiresAt);
      this.currentClientId = response.clientId;

      console.log('[MQTT] JWT authentication successful');
      console.log('[MQTT] Token expires at:', this.tokenExpiresAt);
    } catch (error) {
      console.error('[MQTT] JWT authentication failed:', error);
      throw new Error(`MQTT authentication failed: ${error.message}`);
    }
  }

  private async ensureValidToken(userId: string): Promise<void> {
    const now = new Date();
    const bufferTime = 5 * 60 * 1000; // 5 minutes buffer

    if (!this.jwtToken || !this.tokenExpiresAt ||
      (this.tokenExpiresAt.getTime() - now.getTime()) < bufferTime) {
      console.log('[MQTT] Token expired or near expiry, refreshing...');
      await this.authenticateMqtt(userId);
    }
  }

  public async connect(userId: string, accessToken?: string): Promise<void> {
    // Check circuit breaker
    if (this.isCircuitBreakerOpen) {
      const timeSinceLastAttempt = Date.now() - this.lastConnectionAttempt;
      if (timeSinceLastAttempt < this.circuitBreakerTimeout) {
        console.log('[MQTT] Circuit breaker is open. Connection attempts suspended for',
          Math.round((this.circuitBreakerTimeout - timeSinceLastAttempt) / 1000), 'more seconds');
        return;
      } else {
        console.log('[MQTT] Circuit breaker reset. Allowing new connection attempt.');
        this.isCircuitBreakerOpen = false;
        this.connectionAttempts = 0;
      }
    }

    // Check max attempts
    if (this.connectionAttempts >= this.maxConnectionAttempts) {
      console.warn('[MQTT] Maximum connection attempts reached. Opening circuit breaker.');
      this.isCircuitBreakerOpen = true;
      this.lastConnectionAttempt = Date.now();
      return;
    }

    // Check if already connected or connecting
    if (this.isConnecting || (this.client && this.client.connected)) {
      console.log('[MQTT] Already connected, reconnecting, or disconnecting. Skipping connection. State:', {
        connected: this.client?.connected || false,
        reconnecting: this.client?.reconnecting || false,
        disconnecting: this.client?.disconnecting || false
      });
      return;
    }

    // Implement exponential backoff
    const timeSinceLastAttempt = Date.now() - this.lastConnectionAttempt;
    const retryDelay = Math.min(
      this.baseRetryDelay * Math.pow(2, this.connectionAttempts),
      this.maxRetryDelay
    );

    if (this.connectionAttempts > 0 && timeSinceLastAttempt < retryDelay) {
      const remainingDelay = retryDelay - timeSinceLastAttempt;
      console.log(`[MQTT] Exponential backoff: waiting ${Math.round(remainingDelay / 1000)}s before retry (attempt ${this.connectionAttempts + 1}/${this.maxConnectionAttempts})`);
      setTimeout(() => this.connect(userId, accessToken).catch(console.error), remainingDelay);
      return;
    }

    // Ensure we have a valid JWT token before connecting
    try {
      await this.ensureValidToken(userId);
    } catch (error) {
      console.error('[MQTT] Failed to obtain JWT token:', error);
      this.handleConnectionFailure();
      return;
    }

    this.connectionAttempts++;
    this.lastConnectionAttempt = Date.now();
    this.isConnecting = true;

    console.log(`[MQTT] Attempting connection ${this.connectionAttempts}/${this.maxConnectionAttempts} to:`, this.config?.brokerUrl);

    if (!this.config) {
      console.error('[MQTT] Configuration not set');
      this.isConnecting = false;
      return;
    }

    const mqttBrokerUrl = appConfig.MQTT_BROKER_URL;
    console.log('[MQTT] Connecting to broker...', mqttBrokerUrl)
    this.userId = userId;
    this.config = {
      brokerUrl: mqttBrokerUrl,
      userId
    };

    // Client ID is now generated by the backend JWT service during authentication
    console.log('[MQTT] Using client ID from JWT token:', this.currentClientId);

    try {
      // Base connection options compatible with both custom and public brokers
      const connectionOptions: any = {
        clientId: this.currentClientId,
        clean: true,
        reconnectPeriod: 0, // Disable automatic reconnection - we'll handle it manually
        connectTimeout: 15000, // Reduced timeout for faster failure detection
        keepalive: 60,
        rejectUnauthorized: false,
        protocolVersion: 4,
      };

      // Add JWT authentication as per EMQX examples
      console.log('[MQTT] Using JWT authentication');
      connectionOptions.username = userId;
      connectionOptions.password = this.jwtToken;

      // Add browser-specific options
      if (typeof window !== 'undefined') {
        connectionOptions.reschedulePings = true;
        connectionOptions.properties = {
          maximumPacketSize: 65535
        };
        // Always validate SSL certificates for security
        connectionOptions.rejectUnauthorized = true;
      }

      console.log('[MQTT] Connection options:', {
        ...connectionOptions,
        password: connectionOptions.password ? '***' : undefined
      });

      this.client = mqtt.connect(this.config.brokerUrl, connectionOptions);

      this.setupEventHandlers();
    } catch (error) {
      console.error('[MQTT] Connection error:', error);
      this.handleConnectionFailure();
    }
  }

  public disconnect(): void {
    if (this.client) {
      console.log('[MQTT] Disconnecting client:', this.currentClientId);
      this.cleanupClient();
    }

    this.client = null;
    this.currentClientId = null;
    this.isConnecting = false;
    this.jwtToken = null;
    this.tokenExpiresAt = null;
  }

  private cleanupClient(): void {
    if (!this.client) return;

    console.log('[MQTT] Cleaning up client listeners and connection');

    // Remove all event listeners to prevent memory leaks
    this.client.removeAllListeners();

    // Force disconnect
    this.client.end(true);

    // Clear subscription tracking
    this.subscribedTopics.clear();

    this.isConnecting = false;
  }

  public onMessage(eventType: string, handler: (data: any) => void): void {
    this.messageHandlers.set(eventType, handler);
  }

  private setupEventHandlers(): void {
    if (!this.client) return;

    this.client.on('connect', (connack) => {
      console.log('[MQTT] ✅ Connected to broker successfully');

      // Reset connection tracking on successful connection
      this.connectionAttempts = 0;
      this.isCircuitBreakerOpen = false;
      this.isConnecting = false;

      // Set up subscriptions
      this.subscribeToTopics();
    });

    this.client.on('error', (error) => {
      console.warn('[MQTT] Connection error:', error.message);
      this.handleConnectionFailure();

      // Only log detailed error for first few attempts to reduce spam
      if (this.connectionAttempts <= 3) {
        console.error('[MQTT] Detailed error info:', {
          message: error.message,
          code: (error as any).code,
          errno: (error as any).errno,
          syscall: (error as any).syscall
        });
      }
    });

    this.client.on('offline', () => {
      if (this.connectionAttempts <= 3) {
        console.warn('[MQTT] Client is offline');
      }
    });

    this.client.on('reconnect', () => {
      // This shouldn't fire since we disabled automatic reconnection
      console.log('[MQTT] Attempting to reconnect...');
    });

    this.client.on('close', () => {
      if (this.connectionAttempts <= 3) {
        console.log('[MQTT] Connection closed');
      }
      this.isConnecting = false;
    });

    this.client.on('message', (topic, message) => {
      try {
        const messageString = message.toString();
        let messageData: any;
        if (messageString === "") {
          messageData = { status: 'disconnected' };
        } else {
          messageData = JSON.parse(messageString);
        }

        this.handleMessage(topic, messageData);
      } catch (error) {
        console.error('[MQTT] Error parsing or handling message:', error, "Topic:", topic, "Message:", message.toString());
      }
    });
  }

  private subscribeToTopics(): void {
    if (!this.client || !this.userId) return;

    const userTopics = [
      `legal-assistant/users/${this.userId}/downstream/+`,  // General user updates
      `legal-assistant/users/${this.userId}/requests/+`, // Request topics for request-response pattern
      `legal-assistant/global/broadcast/+`, // Global broadcast topics
    ];

    userTopics.forEach(topic => {
      this.client?.subscribe(topic, (err) => {
        if (err) {
          console.error(`[MQTT] Failed to subscribe to ${topic}:`, err);
        } else {
          console.log(`[MQTT] Subscribed to ${topic}`);
          this.subscribedTopics.add(topic);
        }
      });
    });
  }

  private handleMessage(topic: string, messageData: any): void {
    console.log(`[MQTT] Received message on topic ${topic}:`, messageData);

    // Handle request-response pattern
    if (topic.includes('/requests/')) {
      this.handleRequest(topic, messageData);
      return;
    }

    // Handle legal query progress updates
    if (topic.includes('/legal-query-progress') && messageData?.eventType === 'legal-query-progress') {
      this.handleLegalQueryProgress(messageData.data);
      return;
    }

    // DELETED - court-research feature removed
    // if (topic.includes('/court-research-progress') && messageData?.eventType === 'court-research-progress') { ... }

    // Map MQTT topic to event type for regular updates
    const eventType = this.extractEventTypeFromTopic(topic);

    if (eventType) {
      console.log(`[MQTT] Mapped topic ${topic} to event type: ${eventType}`);

      // Handle global broadcast messages specially
      if (eventType === 'global-broadcast') {
        const actualEventType = messageData.eventType;
        if (actualEventType) {
          console.log(`[MQTT] Global broadcast event type: ${actualEventType}`);

          const socketIOMessage = this.convertToSocketIOFormat(messageData.data || messageData, actualEventType);

          const handler = this.messageHandlers.get(actualEventType);
          if (handler) {
            console.log(`[MQTT] Dispatching global broadcast ${actualEventType} event to handler`);
            handler(socketIOMessage);
          } else {
            console.warn(`[MQTT] No handler found for global broadcast event type: ${actualEventType}`);
          }
        } else {
          console.warn(`[MQTT] Global broadcast message missing eventType:`, messageData);
        }
        return;
      }

      // Convert MQTT message to Socket.IO format
      const socketIOMessage = this.convertToSocketIOFormat(messageData, eventType);

      // Trigger the appropriate handler
      const handler = this.messageHandlers.get(eventType);
      if (handler) {
        console.log(`[MQTT] Dispatching ${eventType} event to handler`);
        handler(socketIOMessage);
      } else {
        console.warn(`[MQTT] No handler found for event type: ${eventType}`);
      }
    } else {
      console.warn(`[MQTT] Could not extract event type from topic: ${topic}`);
    }
  }

  private handleRequest(topic: string, requestData: MQTTRequestEvent): void {
    console.log(`[MQTT] Handling request on topic ${topic}:`, requestData);

    // Extract requestId from topic: legal-assistant/users/{userId}/requests/{requestId}
    const parts = topic.split('/');
    const requestId = parts[4];

    if (!requestId || !requestData.command) {
      console.error(`[MQTT] Invalid request format:`, { requestId, command: requestData.command });
      return;
    }

    // Handle the request and send response
    this.processRequest(requestId, requestData.command, requestData.params || {});
  }

  private async processRequest(requestId: string, command: string, params: any): Promise<void> {
    try {
      console.log(`[MQTT] Processing request ${requestId}: ${command}`);

      let response: any;

      // Handle different commands
      switch (command) {
        case 'ping':
          response = {
            success: true,
            data: {
              message: 'pong',
              timestamp: new Date().toISOString(),
              clientId: this.currentClientId
            }
          };
          break;
        case 'get_status':
          response = {
            success: true,
            data: {
              status: 'connected',
              userId: this.userId,
              clientId: this.currentClientId,
              timestamp: new Date().toISOString()
            }
          };
          break;
        default:
          response = {
            success: false,
            error: `Unknown command: ${command}`
          };
      }

      this.publishResponse(requestId, response);
    } catch (error) {
      console.error(`[MQTT] Error processing request ${requestId}:`, error);
      this.publishResponse(requestId, {
        success: false,
        error: error.message || 'Unknown error'
      });
    }
  }

  private publishResponse(requestId: string, response: any): void {
    if (!this.client || !this.userId) return;

    const responseTopic = `legal-assistant/users/${this.userId}/upstream/responses/${requestId}`;

    const responseMessage: MQTTResponseEvent = {
      requestId,
      success: response.success,
      data: response.data,
      error: response.error,
      _timestamp: Date.now()
    };

    console.log(`[MQTT] Publishing response to ${responseTopic}:`, responseMessage);

    this.client.publish(responseTopic, JSON.stringify(responseMessage), { qos: 1 }, (error) => {
      if (error) {
        console.error(`[MQTT] Failed to publish response:`, error);
      } else {
        console.log(`[MQTT] Response published successfully`);
      }
    });
  }

  private handleLegalQueryProgress(progressData: any): void {
    console.log(`[MQTT] Handling legal query progress:`, progressData);

    if (!progressData?.queryId) {
      console.warn(`[MQTT] Progress data missing queryId:`, progressData);
      return;
    }

    // Dispatch to appropriate Redux handler based on progress stage and status
    const handler = this.messageHandlers.get('legal-query-progress');
    if (handler) {
      handler(progressData);
    } else {
      console.warn(`[MQTT] No handler registered for legal-query-progress events`);
    }
  }

  // DELETED - court-research feature removed
  // private handleCourtResearchProgress(progressData: any): void { ... }

  private extractEventTypeFromTopic(topic: string): string | null {
    // Extract event type from topic structure
    const parts = topic.split('/');

    if (parts.length >= 3 && parts[0] === 'legal-assistant') {
      if (parts[1] === 'global' && parts[2] === 'broadcast') {
        return 'global-broadcast';
      } else if (parts[1] === 'users' && parts[3] === 'downstream') {
        return parts[4] || null; // The path/eventType
      }
    }

    return null;
  }

  private convertToSocketIOFormat(messageData: any, eventType: string): any {
    // Convert MQTT message format to Socket.IO format
    return messageData;
  }

  public isConnected(): boolean {
    return this.client !== null && this.client.connected;
  }

  /**
   * Test MQTT connection and provide diagnostic information
   */
  public async testConnection(): Promise<{ success: boolean; message: string; broker: string }> {
    return new Promise((resolve) => {
      const brokerUrl = this.config?.brokerUrl || appConfig.MQTT_BROKER_URL;

      if (!brokerUrl) {
        resolve({
          success: false,
          message: 'MQTT broker URL not configured',
          broker: 'none'
        });
        return;
      }

      console.log('[MQTT Test] Testing connection to:', brokerUrl);

      const testClientId = `test_${Date.now()}_${Math.random().toString(36).substring(2, 8)}`;
      const testClient = mqtt.connect(brokerUrl, {
        clientId: testClientId,
        clean: true,
        connectTimeout: 10000,
        keepalive: 60,
        rejectUnauthorized: true,
        protocolVersion: 4,
      });

      const timeout = setTimeout(() => {
        testClient.end(true);
        resolve({
          success: false,
          message: 'Connection test timed out after 10 seconds',
          broker: brokerUrl
        });
      }, 10000);

      testClient.on('connect', () => {
        clearTimeout(timeout);
        testClient.end(true);
        resolve({
          success: true,
          message: 'Connection test successful',
          broker: brokerUrl
        });
      });

      testClient.on('error', (error) => {
        clearTimeout(timeout);
        testClient.end(true);
        resolve({
          success: false,
          message: `Connection test failed: ${error.message}`,
          broker: brokerUrl
        });
      });
    });
  }

  public getCurrentSubscriptions(): string[] {
    return Array.from(this.subscribedTopics);
  }

  private handleConnectionFailure(): void {
    this.client = null;
    this.isConnecting = false;

    // Check if we should open circuit breaker
    if (this.connectionAttempts >= this.maxConnectionAttempts) {
      console.warn('[MQTT] Opening circuit breaker after maximum connection attempts');
      this.isCircuitBreakerOpen = true;
      this.lastConnectionAttempt = Date.now();

      // Only clear client ID when circuit breaker opens (indicates we want a fresh start)
      console.log('[MQTT] Clearing client ID due to circuit breaker activation');
      this.currentClientId = null;
    }
    // Note: We preserve currentClientId for normal connection failures to maintain session consistency
  }

  /**
   * Get connection status information
   */
  public getConnectionStatus(): {
    isConnected: boolean;
    isConnecting: boolean;
    connectionAttempts: number;
    maxAttempts: number;
    isCircuitBreakerOpen: boolean;
    timeUntilNextAttempt: number;
  } {
    const timeSinceLastAttempt = Date.now() - this.lastConnectionAttempt;
    const retryDelay = Math.min(
      this.baseRetryDelay * Math.pow(2, this.connectionAttempts),
      this.maxRetryDelay
    );

    let timeUntilNext = 0;
    if (this.isCircuitBreakerOpen) {
      timeUntilNext = Math.max(0, this.circuitBreakerTimeout - timeSinceLastAttempt);
    } else if (this.connectionAttempts > 0 && timeSinceLastAttempt < retryDelay) {
      timeUntilNext = retryDelay - timeSinceLastAttempt;
    }

    return {
      isConnected: this.client?.connected || false,
      isConnecting: this.isConnecting,
      connectionAttempts: this.connectionAttempts,
      maxAttempts: this.maxConnectionAttempts,
      isCircuitBreakerOpen: this.isCircuitBreakerOpen,
      timeUntilNextAttempt: Math.round(timeUntilNext / 1000)
    };
  }

  /**
   * Force reset connection state (for manual retry)
   */
  public resetConnectionState(): void {
    console.log('[MQTT] Manually resetting connection state');
    this.connectionAttempts = 0;
    this.isCircuitBreakerOpen = false;
    this.lastConnectionAttempt = 0;

    if (this.client) {
      this.client.end(true);
      this.client = null;
    }

    this.isConnecting = false;
    this.currentClientId = null;
  }
} 